import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Settings, Send, Eye, EyeOff, CheckCircle, XCircle, 
  AlertTriangle, RefreshCw, Server
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../services/api';

const EmailSettingsTab = () => {
  const [settings, setSettings] = useState({
    provider: 'smtp',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_use_tls: true
  });
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const [resendStatus, setResendStatus] = useState(null);
  const [testingResend, setTestingResend] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchPresets();
    checkResendStatus();
  }, []);

  const checkResendStatus = async () => {
    try {
      const res = await api.get('/settings/email/resend-status');
      setResendStatus(res.data);
    } catch (error) {
      console.error('Failed to check Resend status', error);
      setResendStatus({ configured: false });
    }
  };

  const testResendEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address to test');
      return;
    }
    setTestingResend(true);
    try {
      await api.post('/settings/email/test-resend', { to_email: testEmail });
      toast.success('Test email sent via Resend! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingResend(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings/email');
      setSettings(res.data);
      setTestEmail(res.data.smtp_user || '');
    } catch (error) {
      console.error('Failed to fetch email settings', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresets = async () => {
    try {
      const res = await api.get('/settings/email/presets');
      setPresets(res.data);
    } catch (error) {
      console.error('Failed to fetch presets', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/email', settings);
      toast.success('Email settings saved successfully!');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address to test');
      return;
    }
    
    // Validate SMTP settings are filled
    if (!settings.smtp_host || !settings.smtp_user || !settings.smtp_password) {
      toast.error('Please fill in all SMTP settings (Host, Email, Password) before testing');
      return;
    }
    
    setTesting(true);
    try {
      // Use direct SMTP test endpoint that doesn't require saving first
      await api.post('/settings/email/test-smtp', { 
        to_email: testEmail,
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port || 587,
        smtp_user: settings.smtp_user,
        smtp_password: settings.smtp_password,
        smtp_use_tls: settings.smtp_use_tls !== false
      });
      toast.success('SMTP test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email. Check your SMTP settings.');
    } finally {
      setTesting(false);
    }
  };

  const applyPreset = (presetKey) => {
    const preset = presets[presetKey];
    if (preset) {
      setSettings(prev => ({
        ...prev,
        smtp_host: preset.smtp_host,
        smtp_port: preset.smtp_port,
        smtp_use_tls: preset.smtp_use_tls
      }));
      toast.success(`${preset.name} settings applied. Enter your email and password.`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-[var(--primary)]" />
            Email Settings
          </h2>
          <p className="text-[var(--text-muted)] mt-1">
            Configure email sending for NC Help Hub
          </p>
        </div>
      </div>

      {/* Resend Status - Primary Email Provider */}
      <motion.div 
        className="card p-6 border-2 border-[var(--success)]/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[var(--success)]" />
            Resend Email Service (Primary)
          </h3>
          <span className="px-3 py-1 bg-[var(--success)]/20 text-[var(--success)] text-xs font-medium rounded-full">
            ACTIVE
          </span>
        </div>
        
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-[var(--success)]" />
            <span>Domain verified: <strong>nchelphub.com</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-[var(--success)]" />
            <span>Sending enabled</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[var(--text-muted)]" />
            <span>From: <strong>noreply@nchelphub.com</strong></span>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--border)]">
          <Label className="mb-2 block">Send Test Email (via Resend)</Label>
          <div className="flex gap-2">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address"
              className="input-styled flex-1"
            />
            <Button
              onClick={testResendEmail}
              disabled={testingResend || !testEmail}
              className="btn-primary"
            >
              {testingResend ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* SMTP Backup Configuration Header */}
      <div className="pt-4">
        <h3 className="text-lg font-semibold text-[var(--text-muted)]">
          SMTP Backup Configuration (Optional)
        </h3>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Configure SMTP as a fallback if Resend is unavailable
        </p>
      </div>

      {/* Quick Setup Presets */}
      <motion.div 
        className="card p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Server className="w-5 h-5 text-[var(--primary)]" />
          Quick Setup - Select Your Email Provider
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(presets).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              className="p-4 rounded-xl border border-[var(--border-primary)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/10 transition-all text-left"
            >
              <span className="font-medium">{preset.name}</span>
              <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">
                {preset.instructions?.substring(0, 60)}...
              </p>
            </button>
          ))}
        </div>
      </motion.div>

      {/* SMTP Configuration */}
      <motion.div 
        className="card p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-[var(--primary)]" />
          SMTP Configuration
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>SMTP Server</Label>
            <Input
              value={settings.smtp_host}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
              placeholder="smtp.gmail.com"
              className="input-styled"
            />
          </div>
          <div>
            <Label>Port</Label>
            <Input
              type="number"
              value={settings.smtp_port}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
              placeholder="587"
              className="input-styled"
            />
          </div>
          <div>
            <Label>Email Address</Label>
            <Input
              type="email"
              value={settings.smtp_user}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
              placeholder="your-email@gmail.com"
              className="input-styled"
            />
          </div>
          <div>
            <Label>Password / App Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={settings.smtp_password}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                placeholder="Enter app password"
                className="input-styled pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>From Email (optional)</Label>
            <Input
              type="email"
              value={settings.smtp_from_email}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_from_email: e.target.value }))}
              placeholder="Same as email if empty"
              className="input-styled"
            />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <input
              type="checkbox"
              id="use_tls"
              checked={settings.smtp_use_tls}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_use_tls: e.target.checked }))}
              className="w-4 h-4 rounded border-[var(--border-primary)]"
            />
            <label htmlFor="use_tls" className="text-sm">Use TLS (Recommended)</label>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-4 p-4 bg-[var(--primary)]/10 rounded-lg border border-[var(--primary)]/30">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-[var(--primary)]">Important for Gmail/Outlook:</p>
              <ul className="mt-1 space-y-1 text-[var(--text-muted)]">
                <li>• For Gmail: Enable 2-Step Verification, then create an &quot;App Password&quot; in Google Account settings</li>
                <li>• For Outlook: Use your regular password or create an App Password if 2FA is enabled</li>
                <li>• Never use your regular email password - always use App Passwords</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </motion.div>

      {/* Test SMTP Email */}
      <motion.div 
        className="card p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Send className="w-5 h-5 text-[var(--primary)]" />
          Test SMTP Configuration
        </h3>
        <p className="text-sm text-[var(--text-muted)] mb-4">
          Test your SMTP settings before saving. Enter your email to receive a test message.
        </p>
        <div className="flex gap-3">
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email to receive test"
            className="input-styled flex-1"
          />
          <Button
            onClick={handleTest}
            disabled={testing || !settings.smtp_host || !settings.smtp_user || !settings.smtp_password}
            className="btn-primary"
          >
            {testing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Test
              </>
            )}
          </Button>
        </div>
        {(!settings.smtp_host || !settings.smtp_user || !settings.smtp_password) && (
          <p className="text-sm text-[var(--warning)] mt-2">
            Fill in Host, Email, and Password above to test.
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default EmailSettingsTab;
